import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { merge } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

import { environment } from '@env/environment';
import { Logger, UntilDestroy, untilDestroyed } from '@core';
import { I18nService } from '@app/i18n';

import { CookieService } from 'ngx-cookie-service';
import { v4 as uuid } from 'uuid';
import { CookieConsentService } from './@shared/services/cookieConsent.service';
import { GAService } from './@shared/services/google-analytics.service';

const log = new Logger('App');

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  public ccConsent: boolean;
  public isMobile: boolean;
  public showCookie = true;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private translateService: TranslateService,
    private i18nService: I18nService,
    private cookieService: CookieService,
    private cookieConsentService: CookieConsentService,
    public gaService: GAService
  ) {}

  ngOnInit() {
    this.activatedRoute.queryParams.subscribe((params) => {
      if (params['no-popup']) this.showCookie = params['no-popup'] !== 'true';
    });
    // Setup logger
    if (environment.production) {
      Logger.enableProductionMode();
    }

    this.ccConsent = this.cookieService.check('cookieConsent');
    this.cookieConsentService.onCookieConsentChange = (value) => {
      this.cookieService.set('cookieConsent', value, {
        expires: 2,
        sameSite: 'Strict',
      });
      this.ccConsent = true;
    };

    log.debug('init');

    // Setup translations
    this.i18nService.init(
      environment.defaultLanguage,
      environment.supportedLanguages
    );

    const onNavigationEnd = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd)
    );

    // Change page title on navigation or language change, based on route data
    merge(this.translateService.onLangChange, onNavigationEnd)
      .pipe(
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        switchMap((route) => route.data),
        untilDestroyed(this)
      )
      .subscribe((event) => {
        const title = event.title;
        if (title) {
          this.titleService.setTitle(this.translateService.instant(title));
        }
      });

    if (!this.cookieService.check('userUuid')) {
      this.cookieService.set('userUuid', uuid(), {
        expires: 2000,
        sameSite: 'Strict',
      });
    }
    this.isMobile =
      /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
  }

  ngOnDestroy() {
    this.i18nService.destroy();
  }

  ccAccepted() {
    this.cookieConsentService.notifyCookieConsentChange('accepted');
  }

  ccRefused() {
    this.cookieConsentService.notifyCookieConsentChange('refused');
  }

  /**
   * We use this to send GA Analytics for subcateg.
   * @param {string} eventName
   */
  public sendUpGaEvent(event: string, categ?: string, label?: string) {
    this.gaService.sendEvent(event, categ, label);
  }

  onMoreInfo() {}
}
